/**
 * Dell PowerEdge XE8640 Server Profile
 *
 * Source: research/server-specs.md
 * - 4U form factor
 * - 4-GPU HGX H100 configuration
 * - Dual Intel Xeon Scalable (up to 64 cores each)
 * - Up to 4TB DDR5
 * - 4x 2800W PSU
 * - Liquid-assisted air cooling
 */

import type { ServerProfile } from '../../types/servers';

/**
 * Dell PowerEdge XE8640 with HGX H100 80GB (4-GPU)
 */
export const DELL_XE8640_H100: ServerProfile = {
  id: 'dell-xe8640-h100',
  name: 'Dell PowerEdge XE8640 (HGX H100 4-GPU)',
  role: 'compute',
  vendor: 'Dell',
  model: 'PowerEdge XE8640',

  cpu: {
    sockets: 2,
    coresPerSocket: 48,
    model: 'Intel Xeon Gold 6548+ (5th Gen)',
    tdpPerSocketWatts: 250,
    baseClockGHz: 2.5,
    turboClockGHz: 4.1,
  },

  memory: {
    totalGB: 1024,
    channels: 16,
    type: 'DDR5',
    speedMTs: 4800,
    dimmCount: 32,
    capacityPerDimmGB: 32,
  },

  gpu: {
    platformId: 'hgx-h100-4gpu',
    gpusPerNode: 4,
  },

  expansion: {
    pcieSlots: [
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
      { generation: 5, lanes: 16, formFactor: 'FHFL' },
    ],
    m2Slots: 2,
    drive25Count: 8,
    drive35Count: 0,
  },

  network: {
    onboard1GbE: 2,
    onboard10GbE: 0,
    onboard25GbE: 0,
    bmcPorts: 1,
  },

  power: {
    maxPowerWatts: 11200,
    typicalPowerWatts: 4000,
    idlePowerWatts: 400,
    redundancy: 'N+1',
    psuCount: 4,
    psuCapacityWatts: 2800,
    efficiency: '80 PLUS Titanium',
    inputVoltage: '200-240V AC',
  },

  dimensions: {
    rackUnits: 4,
    widthMm: 482.6,
    depthMm: 800,
    heightMm: 178,
    weightKg: 55,
    maxAltitudeMeters: 3048,
  },

  environmental: {
    tempRange: { min: 10, max: 35 },
    humidityRange: { min: 10, max: 80 },
    maxBtuPerHour: 13648, // 4000W * 3.412
    coolingType: 'liquid',
    noiseLevelDbA: 72,
  },

  metadata: {
    productUrl: 'https://www.dell.com/en-us/shop/ipovw/poweredge-xe8640',
    notes: '4-GPU HGX H100 configuration with liquid-assisted air cooling (no facility water to rack)',
    validated: true,
  },
} as const;
